import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    SelectInput,
    TasksStore,
    toArray,
    uuidv4generate,
    Map,
    LocationSearchInput,
    geocodeLatLng,
} from 'Mri.Web.Common';
import * as React from 'react';
import { Button, Form, FormGroup } from 'reactstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AppStore } from '../Core/AppStore';
import { WarehouseStore } from './WarehouseStore';
import { Marker, Circle } from '@react-google-maps/api';
import goodIcon from '../../../Mri.Web.Common/Assets/Pins/teal-pin.png';
import { BsColumns } from '@react-icons/all-files/bs/BsColumns';

export interface ICreateWarehouseModalProps {
    readonly modalHandle: ModalHandle;
    readonly viewState: 'create' | 'edit';
    readonly warehouseGuid?: string;
}

@autoinject
@observer
export class CreateOrEditWarehouseModal extends React.Component<ICreateWarehouseModalProps> {
    public render() {
        return (
            <Modal
                title={this.title}
                modalHandle={this.props.modalHandle}
                footer={this.footer}
                className="p-0"
                size="lg"
                onOpened={this.onOpenedAsync}
            >
                {this.warehouseForm && !!this.warehouseForm.globalErrors.length && (
                    <div className="alert-danger p-2">
                        <ul>
                            {this.warehouseForm.globalErrors.map((e, idx) => (
                                <li key={idx}>{e}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="container-fluid p-3">
                    {this.warehouseForm && (
                        <Form className="row">
                            <div className="col-12">
                                <h3>Details</h3>

                                <FormGroup color={this.warehouseForm.fields.name.state}>
                                    <label>Name</label>

                                    <Input type="text" id="name" field={this.warehouseForm.fields.name} required />
                                </FormGroup>

                                <FormGroup color={this.warehouseForm.fields.comments.state}>
                                    <label>Comments</label>

                                    <Input
                                        type="text"
                                        id="comments"
                                        field={this.warehouseForm.fields.comments}
                                        required
                                    />
                                </FormGroup>

                                <FormGroup color={this.warehouseForm.fields.accountNumber.state}>
                                    <label>Account</label>

                                    <SelectInput
                                        options={this.accountOptions}
                                        field={this.warehouseForm.fields.accountNumber}
                                        placeholder="Accounts"
                                        required
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-12">
                                <hr />

                                <h3>Location</h3>
                            </div>

                            <div className="col-md-6">
                                <FormGroup color="good">
                                    <label className="control-label">Closest Street Address</label>

                                    <div>
                                        <LocationSearchInput onSelect={this.onSelect} value={this.address} />
                                    </div>
                                </FormGroup>

                                <FormGroup
                                    color={this.warehouseForm.fields.latitude.state}
                                    className="w-50 d-inline-block pe-2"
                                >
                                    <label className="control-label">Latitude</label>
                                    <div>
                                        <Input
                                            type="number"
                                            field={this.warehouseForm.fields.latitude}
                                            placeholder="Latitude"
                                        />
                                    </div>
                                </FormGroup>

                                <FormGroup
                                    color={this.warehouseForm.fields.longitude.state}
                                    className="ps-2 w-50 d-inline-block"
                                >
                                    <label className="control-label">Longitude</label>
                                    <div>
                                        <Input
                                            type="number"
                                            field={this.warehouseForm.fields.longitude}
                                            placeholder="Longitude"
                                        />
                                    </div>
                                </FormGroup>

                                <FormGroup
                                    color={this.warehouseForm.fields.radius.state}
                                    className="w-50 d-inline-block pe-2"
                                >
                                    <label className="control-label">Radius (In Meters)</label>
                                    <div>
                                        <Input
                                            type="number"
                                            field={this.warehouseForm.fields.radius}
                                            placeholder="Radius"
                                        />
                                    </div>
                                </FormGroup>
                            </div>

                            <div className="col-md-6">
                                <Map
                                    dark={this.appStore.storage.theme == 'dark-theme'}
                                    defaultCenter={this.defaultCenter}
                                    zoom={18}
                                    disableRefitBounds
                                    recenter
                                >
                                    {this.warehouseForm.fields.latitude.value &&
                                        this.warehouseForm.fields.longitude.value && (
                                        <Marker
                                            onDragEnd={this.markerMoved}
                                            position={{
                                                lat: this.warehouseForm.fields.latitude.value,
                                                lng: this.warehouseForm.fields.longitude.value,
                                            }}
                                            icon={goodIcon}
                                            draggable
                                        />
                                    )}
                                    {this.warehouseForm.fields.latitude.value &&
                                        this.warehouseForm.fields.longitude.value && (
                                        <Circle
                                            center={{
                                                lat: this.warehouseForm.fields.latitude.value,
                                                lng: this.warehouseForm.fields.longitude.value,
                                            }}
                                            radius={this.warehouseForm.fields.radius.value}
                                            options={{ fillColor: '#13d7ef' }}
                                        />
                                    )}
                                </Map>
                            </div>
                        </Form>
                    )}
                </div>
            </Modal>
        );
    }

    @bind
    @action
    private async onOpenedAsync() {
        this.loading = true;

        try {
            await this.accountsStore.loadAllAsync();
            if (this.props.viewState == 'edit' && this.props.warehouseGuid) {
                await this.warehouseStore.loadAsync(this.props.warehouseGuid);

                if (this.warehouse) {
                    this.warehouseForm = new FormStore(this.warehouse);
                }
            } else {
                this.warehouseForm = new FormStore<Mri.Warehouses.Warehouse>({
                    name: '',
                    latitude: 34.1123969,
                    longitude: -84.195299,
                    radius: 40,
                    warehouseGuid: uuidv4generate(),
                    accountNumber: '',
                    comments: '',
                });
            }

            if (this.warehouseForm) {
                this.geocoderResult = await geocodeLatLng(
                    this.warehouseForm.values.latitude,
                    this.warehouseForm.values.longitude
                );
            }
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    //Private Functions
    @bind
    @action
    private async submitAsync(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (this.warehouseForm && this.warehouseForm.validate()) {
            this.warehouseForm.onSubmit();

            this.loading = true;
            const name = this.warehouseForm.values.name;
            const guid = this.warehouseForm.values.warehouseGuid;

            const task = this.tasksStore.startSessionTask(`${this.viewStateString} Warehouse ${name}`);

            try {
                if (this.props.viewState == 'edit') {
                    await this.warehouseStore.updateAsync(guid, this.warehouseForm.values);
                } else {
                    await this.warehouseStore.createAsync(this.warehouseForm.values);
                }

                this.props.modalHandle.close();
                this.warehouseStore.loadAllAsync();
                this.warehouseForm.clear();
                task.complete();
            } catch (e: any) {
                if (e.modelState) {
                    this.warehouseForm.clearGlobalErrors();
                    this.warehouseForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.loading = false;
            }
        } else {
            console.error('The Form is invalid');
        }
    }

    @bind
    private onSelect(address: string, lat: number, lng: number, geocoderResult: google.maps.GeocoderResult) {
        if (geocoderResult) this.updateLocation(lat, lng, geocoderResult);
    }

    @bind
    private updateLocation(lat: number, lng: number, geocoderResult: google.maps.GeocoderResult) {
        if (this.warehouseForm) {
            const fields = this.warehouseForm.fields;

            fields.latitude.setValue(lat);
            fields.longitude.setValue(lng);
            this.geocoderResult = geocoderResult;
        }
    }

    @computed
    private get address() {
        return this.geocoderResult
            ? this.geocoderResult.formatted_address
            : this.warehouseForm
                ? `${this.warehouseForm.values.latitude}, ${this.warehouseForm.values.longitude}`
                : '';
    }

    @computed
    private get viewStateString() {
        return this.props.viewState == 'create' ? 'Create' : 'Edit';
    }

    @computed
    private get title() {
        return (
            <span className="text-header fw-bold">
                <BsColumns /> {this.viewStateString} Warehouse
            </span>
        );
    }

    @computed
    private get warehouse() {
        return this.props.warehouseGuid ? this.warehouseStore.warehouses.get(this.props.warehouseGuid) : null;
    }

    @computed
    private get footer() {
        return (
            <Button color="primary" onClick={this.submitAsync} disabled={this.loading}>
                <Loader loading={this.loading} showSpinner message={`${this.viewStateString} Warehouse...`} />
                {!this.loading && `${this.viewStateString} Warehouse`}
            </Button>
        );
    }

    @computed
    private get accountOptions() {
        return toArray(this.accountsStore.accounts).map(a => {
            return {
                value: a.accountNumber,
                label: a.accountName,
            };
        });
    }

    @computed
    private get defaultCenter() {
        if (this.warehouseForm) {
            const fields = this.warehouseForm.fields;

            return {
                lat: fields.latitude.value ?? 34.1123969,
                lng: fields.longitude.value ?? -84.195299,
            };
        }
    }

    @bind
    private async markerMoved(data: google.maps.MouseEvent) {
        const lat = data.latLng.lat();
        const lng = data.latLng.lng();

        const geocoderResult = await geocodeLatLng(lat, lng);

        if (geocoderResult) this.updateLocation(lat, lng, geocoderResult);
    }

    @observable
    private loading = false;

    @observable
    private geocoderResult?: google.maps.GeocoderResult;

    @observable
    private warehouseForm?: FormStore<Mri.Warehouses.Warehouse>;

    @inject
    private warehouseStore!: WarehouseStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private appStore!: AppStore;
}
